import { api, realtyTags } from '../api';
import { links } from '../../../data/links';
import { extractPageFromUrl } from '../../../utils/utils';

export const realtiesApi = api.injectEndpoints({
  endpoints: build => ({
    getRealties: build.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: lastPage => extractPageFromUrl(lastPage?.links?.next),
      },
      query: ({
        queryArg: { token, searchType, sortOrder, hideSold },
        pageParam: page,
      }) => {
        let url = `${links.realties}?page=${page}`;

        if (searchType === 'mine') {
          url += '&me';
        }

        if (sortOrder) {
          url += `&sort_order=${sortOrder}`;
        }

        // Hide ALL sold realties (no 60-day grace period)
        if (hideSold) {
          url += '&sold_realty=strict';
        }

        const params = {
          url,
          headers: { Authorization: `Bearer ${token}`}
        };

        return params;
      },
      transformResponse: (responseData, meta, arg) => {
        const realtiesData = responseData?.data;

        if (realtiesData) {
          return responseData;
        } else {
          return {};
        }
      },
      providesTags: result => [
        ...(result?.pages ?? []).flatMap(({ data = [] }) =>
          data.map(({ id }) => ({ type: 'Realties', id }))
        ),
        { type: 'Realties', id: 'LIST' },
      ],
    }),
    getRealty: build.query({
      query: ({ token, id }) => ({
        url: `${links.realties}/${id}`,
        headers: { Authorization: `Bearer ${token}` },
      }),
      transformResponse: responseData => responseData?.data || {},
      providesTags: (result, error, { id }) => [{ type: 'Realties', id }],
    }),
    addRealty: build.mutation({
      query({ token, data }) {
        return {
          url: links.realties,
          method: 'POST',
          body: data,
          headers: { Authorization: `Bearer ${token}` },
        };
      },
      transformResponse: response => response?.data || response,
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
            { type: 'Realties', id: 'LIST' }
          ]
          : [],
    }),
    addRealtyLike: build.mutation({
      query({ token, data }) {
        return {
          url: links.likes,
          method: 'POST',
          body: data,
          headers: { Authorization: `Bearer ${token}` },
        };
      },
      invalidatesTags: (result, error, { data }) =>
        !error
          ? [
            { type: 'Clients', id: 'LIST' },
            { type: 'Requests', id: 'ITEM' },
            { type: 'SearchRealties', id: 'LIST' },
            { type: 'SearchRealties', id: data?.realty_id },
            { type: 'Realties', id: data?.realty_id },
          ]
          : [],
    }),
    updateRealty: build.mutation({
      query({ token, id, data }) {
        return {
          url: `${links.realties}/${id}`,
          method: 'PATCH',
          body: data,
          headers: { Authorization: `Bearer ${token}` },
        };
      },
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
            { type: 'Realties', id },
            { type: 'Realties', id: 'LIST' },
            { type: 'SearchRealties', id },
            { type: 'SearchRealties', id: 'LIST' },
            { type: 'SearchRealtiesMap', id: 'LIST' },
            { type: 'SearchRealtiesInput', id: 'LIST' },
            { type: 'Reminders', id: 'LIST' },
          ]
          : []
    }),
    // action: 'claim' | 'release'. Refetch on error too: 403/409 mean the lead changed hands
    realtyClaim: build.mutation({
      query({ token, id, action }) {
        return {
          url: `${links.realties}/${id}/${action}`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        };
      },
      // LIST too: the realty joins or leaves the agent's own ("Me") list
      invalidatesTags: (result, error, { id }) => [
        ...realtyTags(id),
        { type: 'Realties', id: 'LIST' },
      ],
    }),
    // "i" comment, any agent: replace the whole text, or append a line labelled with the author
    updateRealtyComment: build.mutation({
      query: ({ token, id, comment }) => ({
        url: `${links.realties}/${id}/comment`,
        method: 'PUT',
        body: { comment },
        headers: { Authorization: `Bearer ${token}` },
      }),
      invalidatesTags: (result, error, { id }) => (!error ? realtyTags(id) : []),
    }),
    appendRealtyComment: build.mutation({
      query: ({ token, id, text }) => ({
        url: `${links.realties}/${id}/comment`,
        method: 'POST',
        body: { text },
        headers: { Authorization: `Bearer ${token}` },
      }),
      invalidatesTags: (result, error, { id }) => (!error ? realtyTags(id) : []),
    }),
    // Fiche: kind 'pdf' | 'poster' | 'archive', details 'self' | 'none' → { url } (relative, signed, short-lived)
    getPdfLink: build.mutation({
      query: ({ token, ...body }) => ({
        url: `${links.realties}/pdf-link`,
        method: 'POST',
        body,
        headers: { Authorization: `Bearer ${token}` },
      }),
    }),
    // Admin only. agent_id omitted → return the listing to the import pool
    reassignRealty: build.mutation({
      query({ token, id, agentId }) {
        return agentId
          ? {
            url: `${links.realties}/${id}/reassign`,
            method: 'POST',
            body: { agent_id: agentId },
            headers: { Authorization: `Bearer ${token}` },
          }
          : {
            url: `${links.realties}/${id}/unclaim`,
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          };
      },
      invalidatesTags: (result, error, { id }) => (!error ? realtyTags(id) : []),
    }),
    deleteRealty: build.mutation({
      query({ token, id }) {
        return {
          url: `${links.realties}/${id}`,
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        };
      },
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
            ...realtyTags(id),
            { type: 'Realties', id: 'LIST' },
            { type: 'Reminders', id: 'LIST' },
          ]
          : []
    }),
    pinRealty: build.mutation({
      query: ({ token, id, latitude, longitude }) => ({
        url: `${links.realties}/${id}/pin`,
        method: 'PATCH',
        body: { latitude, longitude },
        headers: { Authorization: `Bearer ${token}` },
      }),
      transformResponse: response => response?.data,
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
            { type: 'Realties', id },
            { type: 'SearchRealtiesMap', id: 'LIST' },
          ]
          : [],
    }),
    unpinRealty: build.mutation({
      query: ({ token, id }) => ({
        url: `${links.realties}/${id}/pin`,
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }),
      transformResponse: response => response?.data,
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
            { type: 'Realties', id },
            { type: 'SearchRealtiesMap', id: 'LIST' },
          ]
          : [],
    }),
    getReverseAddress: build.query({
      query: ({ token, lat, lng }) => ({
        url: `${links.geoReverse}?lat=${lat}&lng=${lng}`,
        headers: { Authorization: `Bearer ${token}` },
      }),
      transformResponse: response => response?.data ?? {},
    }),
    getPinCandidates: build.query({
      query: ({ token, searchQuery }) => {
        const params = new URLSearchParams({
          search_type: '1',
          search_query: searchQuery,
          editable: '1',
        });

        return {
          url: `${links.search}?${params}`,
          headers: { Authorization: `Bearer ${token}` },
        };
      },
      transformResponse: response => response?.data?.realties ?? [],
    }),
  }),
});

export const {
  useGetRealtiesInfiniteQuery,
  useGetRealtyQuery,
  useAddRealtyMutation,
  useAddRealtyLikeMutation,
  useUpdateRealtyMutation,
  useRealtyClaimMutation,
  useGetPdfLinkMutation,
  useUpdateRealtyCommentMutation,
  useAppendRealtyCommentMutation,
  useReassignRealtyMutation,
  useDeleteRealtyMutation,
  usePinRealtyMutation,
  useUnpinRealtyMutation,
  useGetReverseAddressQuery,
  useGetPinCandidatesQuery,
} = realtiesApi;
